import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Paper,
  Portal,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import StopIcon from '@mui/icons-material/Stop';
import SaveIcon from '@mui/icons-material/Save';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import toast from 'react-hot-toast';
import { useScreenRecorderPlacement } from './ScreenRecorderPlacement';
import useScreenRecorder, { MAX_UPLOAD_BYTES, MAX_RECORDING_MS } from '../../hooks/useScreenRecorder';
import { screenRecordingService } from '../../services/api/screenRecording.service';
import { leadAssignmentEnabled } from '../../config/featureFlags';

const UPLOAD_OVERLAY_Z_INDEX = 99999;
const WARN_REMAINING_MS = 60 * 1000;

const formatClock = (ms) => {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const m = String(Math.floor(total / 60)).padStart(2, '0');
  const s = String(total % 60).padStart(2, '0');
  return `${m}:${s}`;
};

const formatBytes = (bytes) => `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

const defaultFileName = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `recording-${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}-${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
};

// Same rule the server applies: letters, digits, space, underscore, hyphen.
const FILE_NAME_RE = /^[A-Za-z0-9 _-]+$/;

const STOP_REASON_MESSAGE = {
  time: `Recording stopped: the ${Math.round(MAX_RECORDING_MS / 60000)} minute limit was reached.`,
  size: `Recording stopped: the ${formatBytes(MAX_UPLOAD_BYTES)} size limit was reached.`,
  browser: 'Recording stopped from the browser.',
};

/**
 * App-level recording UI, mounted once in App.jsx (outside any layout):
 * - top-right pill with elapsed time and a countdown to the recording limit
 * - Save / Discard dialog with a file name after Stop
 * - full-screen blocking overlay with a progress bar while uploading
 */
const ScreenRecorderOverlay = () => {
  const { container } = useScreenRecorderPlacement();
  const { publicSession, publicLinkToken, status, elapsedMs, remainingMs, bytesRecorded, blob, stopReason, stop, reset } = useScreenRecorder();
  const [uploadPct, setUploadPct] = useState(null);
  const [fileName, setFileName] = useState('');
  const [nameError, setNameError] = useState('');
  const [uploadError, setUploadError] = useState('');

  const uploading = uploadPct !== null;
  const recording = status === 'recording';
  const awaitingDecision = status === 'stopped' && !!blob && !uploading;
  const tooLarge = !!blob && blob.size > MAX_UPLOAD_BYTES;
  const nearLimit = recording && remainingMs <= WARN_REMAINING_MS;

  // Seed a default name and explain automatic stops each time a recording finishes.
  useEffect(() => {
    if (blob) {
      setFileName(defaultFileName());
      setUploadError('');
      setNameError('');
      if (stopReason && STOP_REASON_MESSAGE[stopReason]) toast(STOP_REASON_MESSAGE[stopReason], { icon: 'ℹ️' });
    }
  }, [blob, stopReason]);

  const handleStop = useCallback(async () => {
    const out = await stop();
    if (!out || out.size === 0) {
      toast.error('Nothing was recorded.');
      reset();
    }
  }, [stop, reset]);

  const handleDiscard = useCallback(() => {
    reset();
    toast('Recording discarded.');
  }, [reset]);

  const handleSave = useCallback(async () => {
    const name = fileName.trim();
    if (!name) {
      setNameError('Please enter a file name.');
      return;
    }
    if (!FILE_NAME_RE.test(name)) {
      setNameError('Use only letters, numbers, spaces, hyphens and underscores.');
      return;
    }
    if (tooLarge) return;
    setNameError('');
    setUploadError('');
    setUploadPct(0);
    try {
      const result = await screenRecordingService.upload(
        blob,
        { publicSession, publicLinkToken, fileName: name, title: name, pageUrl: window.location.href, durationMs: elapsedMs },
        (pct) => setUploadPct(pct)
      );
      const saved = result?.data;
      toast.success(`Saved as ${saved?.file_name || name} (${formatBytes(saved?.size_bytes || blob.size)}).`);
      reset();
    } catch (e) {
      if (e?.response?.data?.code === 'RECORDING_NAME_EXISTS') {
        setNameError('This filename already exists. Please enter a different filename.');
      }
      setUploadError(e?.response?.data?.message || e?.message || 'Upload failed. Please retry Save.');
    } finally {
      setUploadPct(null);
    }
  }, [blob, fileName, elapsedMs, reset, tooLarge, publicSession, publicLinkToken]);

  if (!leadAssignmentEnabled) return null;

  return (
    <>
      {recording && (
        <Portal container={container || undefined}>
        <Paper
          elevation={6}
          sx={{
            position: container ? 'relative' : 'fixed',
            right: container ? 'auto' : 16,
            top: container ? 'auto' : 12,
            flexWrap: 'wrap',
            zIndex: 2000,
            px: 1.5,
            py: 1,
            borderRadius: 999,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            bgcolor: '#fff',
            border: nearLimit ? '1px solid #ED6C02' : '1px solid transparent',
          }}
        >
          <FiberManualRecordIcon
            sx={{
              color: '#E31E25',
              fontSize: 14,
              animation: 'rec-pulse 1s ease-in-out infinite',
              '@keyframes rec-pulse': { '0%,100%': { opacity: 1 }, '50%': { opacity: 0.25 } },
            }}
          />
          <Typography
            variant="body2"
            sx={{ fontVariantNumeric: 'tabular-nums', fontSize: '0.85rem', fontWeight: 600 }}
          >
            REC {formatClock(elapsedMs)}
          </Typography>
          <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums', fontSize: '0.8rem', color: 'text.secondary' }}>
            · {formatBytes(bytesRecorded)}
          </Typography>
          <Tooltip title={`Recording stops automatically after ${Math.round(MAX_RECORDING_MS / 60000)} minutes`}>
            <Typography
              variant="body2"
              sx={{
                fontVariantNumeric: 'tabular-nums',
                fontSize: '0.8rem',
                fontWeight: nearLimit ? 700 : 500,
                color: nearLimit ? '#ED6C02' : 'text.secondary',
              }}
            >
              · {formatClock(remainingMs)} left
            </Typography>
          </Tooltip>
          <Tooltip title="Stop recording">
            <Button
              size="small"
              variant="contained"
              startIcon={<StopIcon />}
              onClick={handleStop}
              sx={{
                backgroundColor: '#E31E25',
                '&:hover': { backgroundColor: '#B91C22' },
                textTransform: 'none',
                borderRadius: 999,
              }}
            >
              Stop
            </Button>
          </Tooltip>
          <Box sx={{ width: 4 }} />
        </Paper>
        </Portal>
      )}

      <Dialog open={awaitingDecision} maxWidth="xs" fullWidth disableEscapeKeyDown>
        <DialogTitle>Save recording?</DialogTitle>
        <DialogContent>
          {uploadError && <Alert severity="error" sx={{ mb: 2 }}>{uploadError}</Alert>}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Duration {formatClock(elapsedMs)} · Size {blob ? formatBytes(blob.size) : '—'} · Limit {formatBytes(MAX_UPLOAD_BYTES)}
          </Typography>
          {tooLarge && (
            <Typography variant="body2" color="error" sx={{ mb: 2 }}>
              This recording exceeds the {formatBytes(MAX_UPLOAD_BYTES)} upload limit and cannot be saved. Please discard it
              and record a shorter session.
            </Typography>
          )}
          <TextField
            autoFocus
            fullWidth
            label="File name"
            value={fileName}
            onChange={(e) => {
              setFileName(e.target.value);
              if (nameError) setNameError('');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSave();
            }}
            error={!!nameError}
            helperText={nameError || `Saved as ${fileName.trim() || 'recording'}.${blob?.type?.includes('mp4') ? 'mp4' : 'webm'}`}
            disabled={tooLarge}
            inputProps={{ maxLength: 120 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleDiscard} startIcon={<DeleteOutlineIcon />} color="inherit" sx={{ textTransform: 'none' }}>
            Discard
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={tooLarge}
            sx={{ textTransform: 'none' }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Blocks every click on the page until the upload finishes. */}
      <Backdrop
        open={uploading}
        sx={{ zIndex: UPLOAD_OVERLAY_Z_INDEX, color: '#fff', backgroundColor: 'rgba(0, 0, 0, 0.6)' }}
      >
        <Paper elevation={8} sx={{ p: 3, minWidth: 360, maxWidth: '90vw', borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
            {uploadPct > 0 ? <CloudUploadIcon color="primary" /> : <CircularProgress size={22} />}
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Uploading recording…
            </Typography>
          </Box>
          <LinearProgress
            variant={uploadPct > 0 ? 'determinate' : 'indeterminate'}
            value={uploadPct || 0}
            sx={{ height: 8, borderRadius: 4 }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {fileName.trim()} · {blob ? formatBytes(blob.size) : ''}
            </Typography>
            <Typography variant="caption" sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>
              {uploadPct}%
            </Typography>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Please wait. Do not close or refresh this page.
          </Typography>
        </Paper>
      </Backdrop>
    </>
  );
};

export default ScreenRecorderOverlay;
